import { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { SyncStatus } from '@/enums/syncStatus';
import type { Note, NoteId } from '@/types/note';
import { PersistentStore } from '@/services/cache/PersistentStore';
import { WriteBackScheduler } from '@/services/cache/writeBackScheduler';
import { getNotesApi } from '@/services/api/notesApi';
import type { NotesApi, UpdatePatch } from '@/services/api/notesApi';
import { ConflictError } from '@/services/api/errors';
import { ActionType } from '@/enums/actionType';
import { hydrate, reconcileFromServer, rollbackOptimistic } from './notesActions';
import { initialNotesState, notesReducer } from './notesReducer';
import {
  NotesDispatchContext,
  NotesRuntimeContext,
  NotesStateContext,
} from './NotesContext';
import type { NotesRuntime } from './NotesContext';

export interface NotesProviderProps {
  readonly persistentStore?: PersistentStore;
  readonly api?: NotesApi;
}

/**
 * Wires the reducer to persistence and the mock API:
 *   dispatch → reducer → optimistic UI
 *                     → mark cache dirty (writeBackScheduler)
 *                     → fire API call
 *                          success → reconcile
 *                          failure → rollback
 *
 * Loads localStorage synchronously on mount (instant paint) and then
 * reconciles asynchronously with the API to pick up newer server state.
 */
export function NotesProvider({
  children,
  persistentStore,
  api,
}: PropsWithChildren<NotesProviderProps>) {
  const persistent = useMemo(() => persistentStore ?? new PersistentStore(), [persistentStore]);
  const notesApi = useMemo(() => api ?? getNotesApi(), [api]);

  const [state, dispatchRaw] = useReducer(notesReducer, initialNotesState);
  const [runtime, setRuntime] = useState<NotesRuntime>({
    syncStatus: SyncStatus.Idle,
    lastError: null,
  });

  const previousSnapshotsRef = useRef<Map<NoteId, Note | null>>(new Map());
  const stateRef = useRef(state);
  stateRef.current = state;

  const scheduler = useMemo(
    () =>
      new WriteBackScheduler({
        flush: (notes) => {
          persistent.save(notes);
        },
        getAllNotes: () => Object.values(stateRef.current.byId),
      }),
    [persistent],
  );

  // Hydration: instant from localStorage, then reconcile from API.
  useEffect(() => {
    const outcome = persistent.load();
    if (outcome.notes.length > 0) dispatchRaw(hydrate(outcome.notes));

    let cancelled = false;
    const controller = new AbortController();
    setRuntime((r) => ({ ...r, syncStatus: SyncStatus.Syncing }));
    notesApi
      .list(controller.signal)
      .then((notes) => {
        if (cancelled) return;
        dispatchRaw(hydrate(notes));
        setRuntime({ syncStatus: SyncStatus.Idle, lastError: null });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setRuntime({ syncStatus: SyncStatus.Error, lastError: describeError(e) });
      });

    return () => {
      cancelled = true;
      controller.abort();
      scheduler.dispose();
    };
    // scheduler + persistent + notesApi are memoized, so mount-only is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Middleware: after every dispatch, mirror the mutation to the API and
  // schedule a debounced localStorage write. We wrap `dispatch` to peek at the
  // action shape and fire the right API call.
  const dispatch = useMemo(() => {
    return (action: Parameters<typeof dispatchRaw>[0]) => {
      const beforeState = stateRef.current;
      dispatchRaw(action);

      // Non-server-side actions that don't require an API call.
      if (
        action.type === ActionType.Hydrate ||
        action.type === ActionType.BringToFront ||
        action.type === ActionType.ReconcileFromServer ||
        action.type === ActionType.RollbackOptimistic ||
        action.type === ActionType.Undo ||
        action.type === ActionType.Redo
      ) {
        // Still persist locally after undo/redo (state changed).
        if (action.type === ActionType.Undo || action.type === ActionType.Redo) {
          scheduler.markDirty();
        }
        return;
      }

      scheduler.markDirty();

      switch (action.type) {
        case ActionType.Create: {
          const id = action.payload.id;
          previousSnapshotsRef.current.set(id, null);
          setRuntime((r) => ({ ...r, syncStatus: SyncStatus.Syncing }));
          notesApi
            .create({
              x: action.payload.x,
              y: action.payload.y,
              width: action.payload.width,
              height: action.payload.height,
              color: action.payload.color,
              text: action.payload.text,
            })
            .then((serverNote) => {
              // Server assigns a real id; reconcile by removing local placeholder
              // and inserting the server-authored note.
              dispatchRaw({
                type: ActionType.RollbackOptimistic,
                payload: { id, previous: null },
              });
              dispatchRaw(reconcileFromServer(serverNote));
              setRuntime({ syncStatus: SyncStatus.Idle, lastError: null });
            })
            .catch((e: unknown) => {
              dispatchRaw(rollbackOptimistic(id, null));
              setRuntime({ syncStatus: SyncStatus.Error, lastError: describeError(e) });
            });
          break;
        }
        case ActionType.Move:
        case ActionType.Resize:
        case ActionType.UpdateText:
        case ActionType.UpdateColor: {
          const id = action.payload.id;
          const previous = beforeState.byId[id] ?? null;
          previousSnapshotsRef.current.set(id, previous);
          if (!previous) return;
          const patch: UpdatePatch = extractPatch(action);
          setRuntime((r) => ({ ...r, syncStatus: SyncStatus.Syncing }));
          notesApi
            .update(id, patch, previous.version)
            .then((serverNote) => {
              dispatchRaw(reconcileFromServer(serverNote));
              setRuntime({ syncStatus: SyncStatus.Idle, lastError: null });
            })
            .catch((e: unknown) => {
              if (e instanceof ConflictError) {
                // Server has newer state; fetch and reconcile rather than rollback blindly.
                notesApi
                  .get(id)
                  .then((fresh) => {
                    dispatchRaw(reconcileFromServer(fresh));
                    setRuntime({
                      syncStatus: SyncStatus.Stale,
                      lastError: 'reconciled from server',
                    });
                  })
                  .catch(() => {
                    dispatchRaw(rollbackOptimistic(id, previous));
                    setRuntime({ syncStatus: SyncStatus.Error, lastError: describeError(e) });
                  });
              } else {
                dispatchRaw(rollbackOptimistic(id, previous));
                setRuntime({ syncStatus: SyncStatus.Error, lastError: describeError(e) });
              }
            });
          break;
        }
        case ActionType.Delete: {
          const id = action.payload.id;
          const previous = beforeState.byId[id] ?? null;
          if (!previous) return;
          previousSnapshotsRef.current.set(id, previous);
          // Only call the server for notes that were persisted (version > 0).
          if (previous.version === 0) return;
          setRuntime((r) => ({ ...r, syncStatus: SyncStatus.Syncing }));
          notesApi
            .delete(id, previous.version)
            .then(() => setRuntime({ syncStatus: SyncStatus.Idle, lastError: null }))
            .catch((e: unknown) => {
              dispatchRaw(rollbackOptimistic(id, previous));
              setRuntime({ syncStatus: SyncStatus.Error, lastError: describeError(e) });
            });
          break;
        }
      }
    };
  }, [notesApi, scheduler]);

  return (
    <NotesStateContext.Provider value={state}>
      <NotesRuntimeContext.Provider value={runtime}>
        <NotesDispatchContext.Provider value={dispatch}>
          {children}
        </NotesDispatchContext.Provider>
      </NotesRuntimeContext.Provider>
    </NotesStateContext.Provider>
  );
}

function extractPatch(
  action: Extract<
    Parameters<typeof notesReducer>[1],
    | { type: typeof ActionType.Move }
    | { type: typeof ActionType.Resize }
    | { type: typeof ActionType.UpdateText }
    | { type: typeof ActionType.UpdateColor }
  >,
): UpdatePatch {
  switch (action.type) {
    case ActionType.Move:
      return { x: action.payload.x, y: action.payload.y };
    case ActionType.Resize:
      return {
        width: action.payload.width,
        height: action.payload.height,
        ...(action.payload.x !== undefined ? { x: action.payload.x } : {}),
        ...(action.payload.y !== undefined ? { y: action.payload.y } : {}),
      };
    case ActionType.UpdateText:
      return { text: action.payload.text };
    case ActionType.UpdateColor:
      return { color: action.payload.color };
  }
}

function describeError(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}
